import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, GraduationCap, BookOpen, Users, Calendar } from 'lucide-react';

const LoginPage = () => {
  const { loginInstructor, loginAdmin, register, resetPassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('instructor'); // 'instructor' or 'admin'
  const [showResetForm, setShowResetForm] = useState(false);
  
  // Login form state - dynamic based on login type
  const [loginForm, setLoginForm] = useState({
    email: '',
    username: '',
    password: '',
  });
  
  // Registration form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    schoolName: '',
    phone: '',
  });

  // Password reset form state
  const [resetForm, setResetForm] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    let result;
    
    if (loginType === 'admin') {
      if (!loginForm.username || !loginForm.password) {
        toast.error('Please fill in username and password');
        setLoading(false);
        return;
      }
      result = await loginAdmin(loginForm.username, loginForm.password);
    } else {
      if (!loginForm.email || !loginForm.password) {
        toast.error('Please fill in all fields');
        setLoading(false);
        return;
      }
      result = await loginInstructor(loginForm.email, loginForm.password);
    }
    
    if (result.success) {
      toast.success(`Welcome back to ReflectLogs! 🎉`);
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      toast.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    const result = await register(
      registerForm.name,
      registerForm.email,
      registerForm.password,
      registerForm.schoolName,
      registerForm.phone
    );
    
    if (result.success) {
      toast.success('Welcome to ReflectLogs! Your account has been created. 🎉');
    } else {
      toast.error(result.error || 'Registration failed');
    }
    
    setLoading(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!resetForm.email || !resetForm.newPassword || !resetForm.confirmPassword) {
      toast.error('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    if (resetForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    const result = await resetPassword(
      resetForm.email,
      resetForm.newPassword,
      resetForm.confirmPassword
    );
    
    if (result.success) {
      toast.success(`Password reset successfully for ${result.instructorName}! 🎉`);
      if (result.emailStatus && result.emailStatus.includes('email not configured')) {
        toast.info('📧 Email notifications are not configured. Contact your administrator to enable email confirmations.');
      }
      setShowResetForm(false);
      setResetForm({ email: '', newPassword: '', confirmPassword: '' });
    } else {
      toast.error(result.error || 'Password reset failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Welcome & Features */}
        <div className="space-y-8">
          <div className="text-center lg:text-left">
            <h1 className="text-5xl font-bold text-slate-800 mb-4">
              Reflect<span className="text-blue-600">Logs</span>
            </h1>
            <p className="text-xl text-slate-600 mb-6">
              Professional driving lesson management for modern instructors
            </p>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-4 py-2">
              ✨ Complete Instructor Platform
            </Badge>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={24} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Pupil Management</h3>
                <p className="text-sm text-slate-600">Track learners & progress</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <BookOpen size={24} className="text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Smart Questions</h3>
                <p className="text-sm text-slate-600">Text & scale responses</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar size={24} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Hours Tracking</h3>
                <p className="text-sm text-slate-600">Monitor lesson time</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-2 bg-amber-100 rounded-lg">
                <GraduationCap size={24} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Email Reports</h3>
                <p className="text-sm text-slate-600">Auto-send to learners</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login/Register Forms */}
        <div className="w-full max-w-md mx-auto">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl font-bold text-slate-800">
                Instructor Portal
              </CardTitle>
              <p className="text-slate-600 text-sm">
                Sign in to manage your driving lessons
              </p>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" className="text-sm">Sign In</TabsTrigger>
                  <TabsTrigger value="register" className="text-sm">Register</TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  {/* Login Type Selector */}
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant={loginType === 'instructor' ? 'default' : 'outline'}
                        onClick={() => setLoginType('instructor')}
                        className="h-10"
                      >
                        👨‍🏫 Instructor
                      </Button>
                      <Button
                        type="button"
                        variant={loginType === 'admin' ? 'destructive' : 'outline'}
                        onClick={() => setLoginType('admin')}
                        className="h-10"
                      >
                        🛡️ Admin
                      </Button>
                    </div>
                  </div>

                  {showResetForm ? (
                    /* Password Reset Form */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-800">Reset Password</h3>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowResetForm(false)}
                          className="text-slate-600 hover:text-slate-800"
                        >
                          Back to Login
                        </Button>
                      </div>
                      
                      <form onSubmit={handlePasswordReset} className="space-y-4">
                        <div>
                          <Label htmlFor="reset-email">Email Address</Label>
                          <Input
                            id="reset-email"
                            type="email"
                            value={resetForm.email}
                            onChange={(e) => setResetForm({...resetForm, email: e.target.value})}
                            placeholder="Enter your registered email"
                            required
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="new-password">New Password</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={resetForm.newPassword}
                            onChange={(e) => setResetForm({...resetForm, newPassword: e.target.value})}
                            placeholder="Enter new password (min. 6 characters)"
                            required
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                          <Input
                            id="confirm-new-password"
                            type="password"
                            value={resetForm.confirmPassword}
                            onChange={(e) => setResetForm({...resetForm, confirmPassword: e.target.value})}
                            placeholder="Confirm your new password"
                            required
                          />
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full h-12 text-base bg-green-600 hover:bg-green-700"
                          disabled={loading}
                        >
                          {loading ? 'Resetting Password...' : '🔄 Reset Password'}
                        </Button>
                      </form>
                    </div>
                  ) : (
                    /* Login Form */
                    <form onSubmit={handleLogin} className="space-y-4">
                      {loginType === 'instructor' ? (
                        <div>
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            type="email"
                            value={loginForm.email}
                            onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                            placeholder="instructor@example.com"
                            required
                          />
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="username">Admin Username</Label>
                          <Input
                            id="username"
                            value={loginForm.username}
                            onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                            placeholder="admin"
                            required
                          />
                        </div>
                      )}
                      
                      <div className="relative">
                        <Label htmlFor="password">Password</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={loginForm.password}
                            onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                            placeholder="Enter your password"
                            className="pr-10"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      
                      {/* Forgot Password Link - Only for instructors */}
                      {loginType === 'instructor' && (
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => setShowResetForm(true)}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Forgot Password?
                          </button>
                        </div>
                      )}
                      
                      <Button 
                        type="submit" 
                        className={`w-full h-12 text-base ${loginType === 'admin' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        disabled={loading}
                      >
                        {loading 
                          ? (loginType === 'admin' ? 'Signing In as Admin...' : 'Signing In...')
                          : (loginType === 'admin' ? '🛡️ Admin Sign In' : '👨‍🏫 Instructor Sign In')
                        }
                      </Button>
                    </form>
                  )}
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="reg-name">Full Name *</Label>
                        <Input
                          id="reg-name"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="reg-email">Email *</Label>
                        <Input
                          id="reg-email"
                          type="email"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="school-name">Driving School</Label>
                      <Input
                        id="school-name"
                        value={registerForm.schoolName}
                        onChange={(e) => setRegisterForm({...registerForm, schoolName: e.target.value})}
                        placeholder="School name (optional)"
                      />
                    </div>

                    <div>
                      <Label htmlFor="reg-phone">Phone Number</Label>
                      <Input
                        id="reg-phone"
                        type="tel"
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                        placeholder="Your phone number (optional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="reg-password">Password *</Label>
                        <Input
                          id="reg-password"
                          type="password"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                          placeholder="Min. 6 characters"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm-password">Confirm *</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                          placeholder="Repeat password"
                          required
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base"
                      disabled={loading}
                    >
                      {loading ? 'Creating Account...' : 'Create Instructor Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;